import { z } from 'zod';
import { CurrencySchema, MoneyStringSchema, RatioSchema } from './common.js';

export const QuoteVariableCostInputSchema = z.object({
  tipo: z.enum(['frete', 'bombona', 'comissao', 'instalacao', 'outros']),
  nome: z.string().min(1),
  valorOrcado: MoneyStringSchema,
  caixaKind: z.string().default('variaveis'),
});

export const DiscountInputSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('percentual'), percentual: RatioSchema }),
  z.object({ tipo: z.literal('valor'), valor: MoneyStringSchema }),
]);

export const CreateQuoteSchema = z.object({
  clientId: z.string().uuid(),
  itens: z
    .array(z.object({ productId: z.string().uuid(), litros: z.number().int().positive() }))
    .min(1),
  variaveis: z.array(QuoteVariableCostInputSchema).optional(),
  margem: RatioSchema,
  desconto: DiscountInputSchema.optional(),
  validadeDias: z.number().int().positive().optional(),
  prazoRecebimentoDias: z.number().int().min(0).optional(),
  /**
   * Custos globais (CostItemDef) a aplicar neste orçamento (RF-PRO-04). Vazio/ausente
   * = todos os itens ativos do tenant (comportamento padrão). Matéria-prima e mão de
   * obra são sempre incluídas por serem custos-base do produto.
   */
  costItemDefIds: z.array(z.string().uuid()).optional(),
});
export type CreateQuoteDTO = z.infer<typeof CreateQuoteSchema>;

/**
 * Opção mínima de orçamento para pickers (ex.: vincular viagem — RF-VIA-10) sem expor
 * custos/margem. Acessível a papéis operacionais (colaborador) que não veem o financeiro.
 */
export const QuoteOptionSchema = z.object({
  id: z.string().uuid(),
  numero: z.number().int(),
  clientId: z.string().uuid(),
  status: z.string(),
});
export type QuoteOptionDTO = z.infer<typeof QuoteOptionSchema>;

export const QuoteStatusSchema = z.enum([
  'aberto',
  'aguardando',
  'ganho',
  'faturado',
  'perdido',
  'expirado',
  'estornado',
]);

export const LoseQuoteSchema = z.object({ motivoPerda: z.string().min(1) });

/**
 * Aplica/edita o desconto de um orçamento já criado (antes do faturamento). `desconto: null`
 * remove o desconto (preço final volta ao original). O custo permanece congelado (RF-ORC-15);
 * só o preço/apontamento muda (RF-ORC-06/10).
 */
export const ApplyDiscountSchema = z.object({
  desconto: DiscountInputSchema.nullable(),
});
export type ApplyDiscountDTO = z.infer<typeof ApplyDiscountSchema>;

export const QuoteViewSchema = z.object({
  id: z.string().uuid(),
  numero: z.number(),
  versao: z.number(),
  clientId: z.string().uuid(),
  status: QuoteStatusSchema,
  moeda: CurrencySchema,
  margem: z.number(),
  custoTotal: MoneyStringSchema,
  precoOriginal: MoneyStringSchema,
  precoFinal: MoneyStringSchema,
  descontoValor: MoneyStringSchema.nullable(),
  margemFinal: z.number(),
  validade: z.string().nullable(),
  requerAprovacaoDesconto: z.boolean(),
});
export type QuoteViewDTO = z.infer<typeof QuoteViewSchema>;

/** Linha de composição de custo (uma caixinha que o item/variável alimenta) — RF-ORC-05/RF-FIN-03. */
export const QuoteCaixinhaSchema = z.object({
  caixaKind: z.string(),
  nome: z.string(),
  valor: MoneyStringSchema,
});
export type QuoteCaixinhaDTO = z.infer<typeof QuoteCaixinhaSchema>;

/** Item do orçamento discriminado: produto, litros, custo/100 L, total e % do total + composição. */
export const QuoteItemViewSchema = z.object({
  productId: z.string().uuid().nullable(),
  descricao: z.string(),
  litros: z.number().int(),
  custoBase100L: MoneyStringSchema,
  custoLinha: MoneyStringSchema,
  percentualCusto: z.number(),
  composicao: z.array(QuoteCaixinhaSchema),
});
export type QuoteItemViewDTO = z.infer<typeof QuoteItemViewSchema>;

export const QuoteVariableCostViewSchema = z.object({
  tipo: z.string(),
  nome: z.string(),
  valorOrcado: MoneyStringSchema,
  valorExecutado: MoneyStringSchema.nullable(),
  caixaKind: z.string(),
});
export type QuoteVariableCostViewDTO = z.infer<typeof QuoteVariableCostViewSchema>;

/** Detalhe completo do orçamento (documento): agregados + itens discriminados + variáveis + caixinhas geradas. */
export const QuoteDetailSchema = QuoteViewSchema.extend({
  totalLitros: z.number().int(),
  itens: z.array(QuoteItemViewSchema),
  variaveis: z.array(QuoteVariableCostViewSchema),
  /** Composição agregada por caixinha — o que a segregação (RF-FIN-03) reservará ao faturar. */
  caixinhas: z.array(QuoteCaixinhaSchema),
});
export type QuoteDetailDTO = z.infer<typeof QuoteDetailSchema>;
